import React, { useEffect, useState } from "react";
import { Button, Typography } from "@mui/material";
import { Check, X } from "phosphor-react";
import { fget, fpatch } from "../../../../API/callsAPI";
import CustomDropdown from "../../../Utilities/CustomDropdown/CustomDropdown.component";
import useStyles from "./NavigationSettingsCommon.styles";
import NavigationSettingsCommon from "./NavigationSettingsCommon";
import {
  buildConfigItems,
  buildPageGroupFromSelection,
  parseNavConfigItems,
} from "./navigationSettingsUtils";

const PROJECT_ROLE_OPTIONS = [
  { title: "Admin", value: "admin" },
  { title: "User", value: "user" },
  { title: "Reviewer", value: "reviewer" },
  { title: "Freelancer", value: "freelancer" },
];

const RoleBasedNavigation = ({ setSnackValue, projectId }) => {
  const classes = useStyles();
  const [selectedProjectRole, setSelectedProjectRole] = useState("admin");
  const [pages, setPages] = useState([]);
  const [customPages, setCustomPages] = useState([]);
  const [navGroups, setNavGroups] = useState([]);
  const [initialConfig, setInitialConfig] = useState(null);
  const [navOrder, setNavOrder] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (projectId) {
      getNavConfig(selectedProjectRole);
    }
  }, [projectId, selectedProjectRole]);

  const buildPatchPayload = (items) => ({
    project: projectId,
    role: selectedProjectRole,
    config: {
      items,
      show_page_names: initialConfig?.config?.show_page_names ?? true,
    },
  });

  const patchNavConfig = async (items) => {
    if (!initialConfig?.id) {
      return;
    }

    setIsSaving(true);

    try {
      await fpatch({
        url: `/project/${projectId}/navbar-config/${initialConfig.id}/`,
        data: buildPatchPayload(items),
      });

      setSnackValue({
        isOpen: true,
        message: "Changes saved successfully",
        isError: false,
      });
      await getNavConfig(selectedProjectRole);
    } catch (error) {
      setSnackValue({
        isOpen: true,
        message: `${error}`,
        isError: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getNavConfig = async (role) => {
    try {
      const response = await fget({
        url: `/project/${projectId}/navbar-configs/?role=${role}`,
      });
      const data = response.data.results?.[0] || response.data;
      const items = data?.config?.items || [];
      const parsedItems = parseNavConfigItems(items);

      setInitialConfig(data);
      setPages(parsedItems.pages);
      setCustomPages(parsedItems.customPages);
      setNavGroups(parsedItems.navGroups);
      setNavOrder(parsedItems.navOrder);
      setIsDirty(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handlePageToggle = (pageId) => {
    setIsDirty(true);
    setPages((prevPages) => {
      const updatedPages = prevPages.map((page) =>
        page.key === pageId ? { ...page, enabled: !page.enabled } : page,
      );

      setNavOrder((prevOrder) => {
        const toggledPage = updatedPages.find((page) => page.key === pageId);

        if (!toggledPage.enabled) {
          return prevOrder.filter((item) => item.key !== pageId);
        }

        if (prevOrder.some((item) => item.key === pageId)) {
          return prevOrder;
        }

        return [...prevOrder, toggledPage];
      });

      return updatedPages;
    });
  };

  const handleCustomPageToggle = (pageId) => {
    setIsDirty(true);
    setCustomPages((prevPages) => {
      const updatedPages = prevPages.map((page) =>
        page.key === pageId ? { ...page, enabled: !page.enabled } : page,
      );

      setNavOrder((prevOrder) => {
        const toggledPage = updatedPages.find((page) => page.key === pageId);

        if (!toggledPage.enabled) {
          return prevOrder.filter((item) => item.key !== pageId);
        }

        if (prevOrder.some((item) => item.key === pageId)) {
          return prevOrder;
        }

        return [...prevOrder, toggledPage];
      });

      return updatedPages;
    });
  };

  const handleGroupToggle = (groupKey) => {
    setIsDirty(true);
    setNavGroups((prevGroups) => {
      const updatedGroups = prevGroups.map((group) =>
        group.key === groupKey ? { ...group, enabled: !group.enabled } : group,
      );

      setNavOrder((prevOrder) => {
        const toggledGroup = updatedGroups.find(
          (group) => group.key === groupKey,
        );

        if (!toggledGroup.enabled) {
          return prevOrder.filter((item) => item.key !== groupKey);
        }

        if (prevOrder.some((item) => item.key === groupKey)) {
          return prevOrder;
        }

        return [...prevOrder, toggledGroup];
      });

      return updatedGroups;
    });
  };

  const handleNavReorder = (newOrder) => {
    setIsDirty(true);
    setNavOrder(newOrder);
  };

  const handleDiscardChanges = () => {
    getNavConfig(selectedProjectRole);
  };

  const handleSaveChanges = async () => {
    await patchNavConfig(
      buildConfigItems(navOrder, pages, customPages, navGroups),
    );
  };

  const handleAddCustomPage = async (selectedPages) => {
    const newCustomPages = selectedPages.map((page) => ({
      key: page.id,
      label: page.name,
      enabled: true,
      type: "customPage",
    }));

    const updatedNavOrder = [...navOrder, ...newCustomPages];

    await patchNavConfig(
      buildConfigItems(
        updatedNavOrder,
        pages,
        [...customPages, ...newCustomPages],
        navGroups,
      ),
    );
  };

  const handleDeleteCustomPage = async (pageId) => {
    setIsDirty(true);
    const updatedCustomPages = customPages.filter(
      (page) => page.key !== pageId,
    );
    const newNavOrder = navOrder.filter((item) => item.key !== pageId);

    await patchNavConfig(
      buildConfigItems(newNavOrder, pages, updatedCustomPages, navGroups),
    );
  };

  const handleAddPageGroup = async ({ name, iconId, pageIds }) => {
    const newPageGroup = buildPageGroupFromSelection(
      name,
      iconId,
      pageIds,
      true,
      pages,
      customPages,
    );
    const updatedNavOrder = [...navOrder, newPageGroup];

    await patchNavConfig(
      buildConfigItems(updatedNavOrder, pages, customPages, [
        ...navGroups,
        newPageGroup,
      ]),
    );
  };

  const handleEditPageGroup = async ({ name, iconId, pageIds, groupKey }) => {
    const existingGroup = navGroups.find((group) => group.key === groupKey);

    if (!existingGroup) {
      return;
    }

    const updatedPageGroup = buildPageGroupFromSelection(
      name,
      iconId,
      pageIds,
      existingGroup.enabled,
      pages,
      customPages,
    );

    const updatedNavGroups = navGroups.map((group) =>
      group.key === groupKey ? updatedPageGroup : group,
    );

    const updatedNavOrder = navOrder.some((item) => item.key === groupKey)
      ? navOrder.map((item) =>
          item.key === groupKey ? updatedPageGroup : item,
        )
      : navOrder;

    await patchNavConfig(
      buildConfigItems(updatedNavOrder, pages, customPages, updatedNavGroups),
    );
  };

  const handleDeletePageGroup = async (groupKey) => {
    setIsDirty(true);
    const updatedNavGroups = navGroups.filter(
      (group) => group.key !== groupKey,
    );
    const newNavOrder = navOrder.filter((item) => item.key !== groupKey);

    await patchNavConfig(
      buildConfigItems(newNavOrder, pages, customPages, updatedNavGroups),
    );
  };

  return (
    <>
      <div className={classes.headerRow}>
        <Typography className={classes.subtitle}>
          Set the default navigation for each project role. Team members can
          still customize their own from My Navigation.
        </Typography>
        <div className={classes.headerActions}>
          <Button
            variant="contained"
            startIcon={<Check />}
            className={classes.saveButton}
            onClick={handleSaveChanges}
            disabled={!isDirty || isSaving}
          >
            Save Changes
          </Button>
          <Button
            variant="outlined"
            startIcon={<X />}
            className={classes.discardButton}
            onClick={handleDiscardChanges}
            disabled={!isDirty || isSaving}
          >
            Discard Changes
          </Button>
        </div>
      </div>
      <div style={{ maxWidth: "420px" }}>
        <Typography fontSize="14px !important">Project Role</Typography>
        <div style={{ marginTop: "8px" }}>
          <CustomDropdown
            value={selectedProjectRole}
            updateValue={setSelectedProjectRole}
            options={PROJECT_ROLE_OPTIONS}
            showNone={false}
          />
        </div>
      </div>
      <NavigationSettingsCommon
        pages={pages}
        customPages={customPages}
        navGroups={navGroups}
        navOrder={navOrder}
        showPageNames={initialConfig?.config?.show_page_names ?? true}
        setShowPageNames={(value) => {
          setIsDirty(true);
          setInitialConfig((prevConfig) => ({
            ...prevConfig,
            config: { ...prevConfig?.config, show_page_names: value },
          }));
        }}
        setSnackValue={setSnackValue}
        projectId={projectId}
        handlePageToggle={handlePageToggle}
        handleCustomPageToggle={handleCustomPageToggle}
        handleGroupToggle={handleGroupToggle}
        handleNavReorder={handleNavReorder}
        handleAddCustomPage={handleAddCustomPage}
        handleDeleteCustomPage={handleDeleteCustomPage}
        handleAddPageGroup={handleAddPageGroup}
        handleEditPageGroup={handleEditPageGroup}
        handleDeletePageGroup={handleDeletePageGroup}
      />
    </>
  );
};

export default RoleBasedNavigation;
